import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Move, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Toggle } from '@/components/ui/toggle';
import { Slider } from '@/components/ui/slider';
import { computeSplitCellRect, clipRectToRatio } from '@/lib/layouts/computeSplitCellRect';
import { deriveClipPosition } from '@/lib/layouts/deriveClipPosition';
import { applyReframePan } from '@/lib/layers/applyReframePan';
import { clampLayerFrame } from '@/lib/layers/clampLayerFrame';
import type { LayerFrame } from '@/lib/layers/types';
import type { SatisfatorioConfig } from '@/lib/layouts/types';
import { createRafScheduler } from '@/lib/dom/createRafScheduler';

const NEUTRAL_FRAME: LayerFrame = { scale: 1, offsetX: 0, offsetY: 0 };
const MIN_SIZE = 0.1;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

interface CellRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

type DragMode =
  | { kind: 'move' }
  | { kind: 'pan' }
  | { kind: 'resize'; corner: 'nw' | 'ne' | 'sw' | 'se' };

interface Props {
  config: SatisfatorioConfig;
  isLandscape: boolean;
  onConfigPatch: (patch: Partial<SatisfatorioConfig>) => void;
}

/** Free-floating move/resize + reframe (zoom/pan) editor for the Satisfying
 *  layout's clip cell — mounted inside CompositionFitOverlay, sibling to
 *  LayerCanvasEditor, sharing the same fractional coordinate space. */
export const SatisfyingCellEditor: React.FC<Props> = ({ config, isLandscape, onConfigPatch }) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{
    mode: DragMode; startX: number; startY: number; rect: CellRect; frame: LayerFrame;
  } | null>(null);
  const [reframeOn, setReframeOn] = useState(false);

  const rect: CellRect = config.clipRect ?? computeSplitCellRect(config, isLandscape);
  const frame: LayerFrame = config.clipFrame ?? NEUTRAL_FRAME;
  const onConfigPatchRef = useRef(onConfigPatch);
  onConfigPatchRef.current = onConfigPatch;
  const patchSchedulerRef = useRef(createRafScheduler(
    (patch: Partial<SatisfatorioConfig>) => onConfigPatchRef.current(patch),
  ));

  const onPointerMove = useCallback((e: PointerEvent) => {
    const d = drag.current;
    if (!d || !containerRef.current) return;
    const box = containerRef.current.getBoundingClientRect();
    const dx = (e.clientX - d.startX) / box.width;
    const dy = (e.clientY - d.startY) / box.height;

    if (d.mode.kind === 'pan') {
      // Fractions of the BOX (the cell's own pixel size), not the container.
      const dxFrac = d.rect.w > 0 ? dx / d.rect.w : 0;
      const dyFrac = d.rect.h > 0 ? dy / d.rect.h : 0;
      patchSchedulerRef.current.schedule({
        clipFrame: applyReframePan({ frame: d.frame, dxFrac, dyFrac }),
      });
      return;
    }

    let { x, y, w, h } = d.rect;
    if (d.mode.kind === 'move') {
      // Preserve size exactly; only the position is clamped so the box
      // can't be shrunk by dragging it near an edge. A dimension already
      // >= 1 (e.g. the default full-width/height split cell) pins to 0.
      x = clamp(d.rect.x + dx, 0, w > 1 ? 0 : 1 - w);
      y = clamp(d.rect.y + dy, 0, h > 1 ? 0 : 1 - h);
    } else {
      const c = d.mode.corner;
      if (c === 'se') { w = d.rect.w + dx; h = d.rect.h + dy; }
      if (c === 'ne') { w = d.rect.w + dx; h = d.rect.h - dy; y = d.rect.y + dy; }
      if (c === 'sw') { w = d.rect.w - dx; h = d.rect.h + dy; x = d.rect.x + dx; }
      if (c === 'nw') { w = d.rect.w - dx; h = d.rect.h - dy; x = d.rect.x + dx; y = d.rect.y + dy; }
      x = clamp(x, 0, 1 - MIN_SIZE);
      y = clamp(y, 0, 1 - MIN_SIZE);
      w = clamp(w, MIN_SIZE, 1 - x);
      h = clamp(h, MIN_SIZE, 1 - y);
    }
    // Keep the legacy split fields in sync with the cell: "Tamanho do Clipe"
    // tracks the size along the split axis, and clipPosition is derived from
    // where the cell sits so the settings toggle, reset path, and
    // enforced-layout merges stay coherent.
    patchSchedulerRef.current.schedule({
      clipRect: { x, y, w, h },
      clipRatio: clipRectToRatio({ w, h }, isLandscape),
      clipPosition: deriveClipPosition({ x, y, w, h }, isLandscape),
    });
  }, [isLandscape]);

  const endDrag = useCallback(() => {
    patchSchedulerRef.current.flush();
    drag.current = null;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', endDrag);
  }, [onPointerMove]);

  const beginDrag = useCallback((mode: DragMode, e: React.PointerEvent) => {
    // Only the primary button drags — right-click belongs to the context menu.
    if (e.button !== 0) return;
    e.stopPropagation();
    drag.current = { mode, startX: e.clientX, startY: e.clientY, rect, frame };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', endDrag);
  }, [rect, frame, onPointerMove, endDrag]);

  const handleReset = useCallback(() => {
    setReframeOn(false);
    onConfigPatch({ clipRect: undefined, clipFrame: undefined });
  }, [onConfigPatch]);

  return (
    <div ref={containerRef} className="absolute inset-0 pointer-events-none">
      <div
        data-testid="satisfying-cell-box"
        onPointerDown={(e) => beginDrag(reframeOn ? { kind: 'pan' } : { kind: 'move' }, e)}
        className={`absolute pointer-events-auto border-2 border-primary ${reframeOn ? 'cursor-grab' : 'cursor-move'}`}
        style={{
          left: `${rect.x * 100}%`, top: `${rect.y * 100}%`,
          width: `${rect.w * 100}%`, height: `${rect.h * 100}%`,
        }}
      >
        {!reframeOn && (['nw', 'ne', 'sw', 'se'] as const).map((corner) => (
          <div
            key={corner}
            onPointerDown={(e) => beginDrag({ kind: 'resize', corner }, e)}
            className="absolute h-3 w-3 rounded-full bg-primary"
            style={{
              left: corner.includes('w') ? -6 : undefined,
              right: corner.includes('e') ? -6 : undefined,
              top: corner.includes('n') ? -6 : undefined,
              bottom: corner.includes('s') ? -6 : undefined,
              cursor: `${corner}-resize`,
            }}
          />
        ))}

        <div
          className="absolute -top-9 left-0 flex items-center gap-1.5 rounded-md bg-background/90 px-1.5 py-1 shadow"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <Toggle
            data-testid="cell-reframe"
            pressed={reframeOn}
            onPressedChange={setReframeOn}
            size="sm"
            variant="outline"
            aria-label={t('projectEditor.layers.reframe')}
          >
            <Move className="h-3.5 w-3.5" />
          </Toggle>
          {reframeOn && (
            <div className="w-24">
              <Slider
                data-testid="cell-zoom"
                value={[frame.scale]}
                min={1} max={3} step={0.1}
                onValueChange={([v]) => onConfigPatch({ clipFrame: clampLayerFrame({ ...frame, scale: v }) })}
              />
            </div>
          )}
          <Button
            data-testid="cell-reset"
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            title={t('projectEditor.layers.resetCell')}
            aria-label={t('projectEditor.layers.resetCell')}
            onClick={handleReset}
          >
            <RotateCcw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SatisfyingCellEditor;
